/**
 * One string comparator for every list a brewer reads.
 *
 * `String.prototype.localeCompare` compares character by character, so a digit
 * run is ordered by its first digit: "Batch 10" files before "Batch 2", and the
 * BJCP code `10A` lands between `1D` and `2A`. Nobody reads numbers that way.
 * A `numeric` collator reads the whole digit run as one number, which is what
 * the eye does.
 *
 * `sensitivity: 'base'` folds case and accents, so "kölsch" sorts with
 * "Kolsch" rather than after every unaccented name — the right behaviour for
 * lists of beer, malt and hop names.
 *
 * The collator is built once at module scope. Constructing one is the expensive
 * part of `Intl`, and these comparators run inside sort callbacks over hundreds
 * of catalog rows.
 *
 * This started life as a private const in the gallery's style filter, which was
 * the only list in the app that ordered numbers correctly. It is here so the
 * other twenty sort sites can be right for the same reason rather than by
 * accident.
 */

const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/**
 * Compare two strings the way a person reads them: digit runs as numbers.
 *
 * ```ts
 * ['Batch 10', 'Batch 2'].sort(naturalCompare) // ['Batch 2', 'Batch 10']
 * ```
 */
export function naturalCompare(a: string, b: string): number {
  return COLLATOR.compare(a, b);
}

/**
 * Compare two records by a string field, then by a stable tiebreak.
 *
 * The tiebreak is what keeps a sort deterministic when two rows share a name —
 * two "Pale Ale" recipes must not swap places between renders.
 */
export function naturalCompareBy<T>(
  select: (item: T) => string,
  tiebreak?: (item: T) => string,
): (a: T, b: T) => number {
  return (a, b) => {
    const byField = naturalCompare(select(a), select(b));
    if (byField !== 0 || tiebreak === undefined) return byField;
    return naturalCompare(tiebreak(a), tiebreak(b));
  };
}
